/**
 * Tiny reusable client-side table sorting used by the tenant_admin
 * list pages (Devices, Procedures, Participants). One hook for the
 * order state, one comparator that sorts a copy by a per-column value
 * accessor with a stable, null-last ordering.
 */
import { useCallback, useMemo, useState } from 'react';

export type SortDir = 'asc' | 'desc';

export interface TableSort<K extends string> {
  orderBy: K;
  order: SortDir;
  /** Toggle direction if the same column, else switch to it (asc). */
  onSort: (key: K) => void;
}

export function useTableSort<K extends string>(
  initialKey: K,
  initialDir: SortDir = 'asc',
): TableSort<K> {
  const [orderBy, setOrderBy] = useState<K>(initialKey);
  const [order, setOrder] = useState<SortDir>(initialDir);
  const onSort = useCallback(
    (key: K) => {
      setOrderBy((prev) => {
        if (prev === key) {
          setOrder((d) => (d === 'asc' ? 'desc' : 'asc'));
          return prev;
        }
        setOrder('asc');
        return key;
      });
    },
    [],
  );
  return { orderBy, order, onSort };
}

export type Cell = string | number | boolean | null | undefined;

/**
 * Sort a copy of `rows` by the value the accessor returns for the
 * active column. Case-insensitive for strings; nulls/undefined sort
 * last regardless of direction; booleans order false < true.
 */
export function sortRows<T>(
  rows: T[],
  accessor: (row: T) => Cell,
  order: SortDir,
): T[] {
  const dir = order === 'asc' ? 1 : -1;
  return [...rows].sort((a, b) => {
    const va = accessor(a);
    const vb = accessor(b);
    const aEmpty = va === null || va === undefined || va === '';
    const bEmpty = vb === null || vb === undefined || vb === '';
    if (aEmpty && bEmpty) return 0;
    if (aEmpty) return 1; // nulls always last
    if (bEmpty) return -1;
    let cmp: number;
    if (typeof va === 'number' && typeof vb === 'number') {
      cmp = va - vb;
    } else if (typeof va === 'boolean' && typeof vb === 'boolean') {
      cmp = va === vb ? 0 : va ? 1 : -1;
    } else {
      cmp = String(va).localeCompare(String(vb), undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    }
    return cmp * dir;
  });
}

/**
 * Sort state plus the sorted rows, for a table whose every column sorts.
 * `accessors` maps each column key to the value that column sorts by; keep
 * it stable (a module-level constant, or memoised) — it is read on every
 * sort but deliberately not a dependency.
 */
export function useSortedRows<T, K extends string>(
  rows: T[],
  accessors: Record<K, (row: T) => Cell>,
  initialKey: NoInfer<K>,
  initialDir: SortDir = 'asc',
): { sort: TableSort<K>; sorted: T[] } {
  const sort = useTableSort<K>(initialKey, initialDir);
  const sorted = useMemo(
    () => sortRows(rows, accessors[sort.orderBy], sort.order),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [rows, sort.orderBy, sort.order],
  );
  return { sort, sorted };
}
